/**
 * JobTargetSection — the "Offre d'emploi cible" collapsible section of the
 * ATS sidebar. Lets the user provide the target job by pasting text, pasting
 * a URL (optionally fetched via the Chrome extension), or just a job title.
 * Also surfaces the structured ParsedJobOffer returned by the AI parser.
 *
 * Pure UI component — all state and handlers come from the parent via props.
 */

import { memo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileEdit,
  Link as LinkIcon,
  Loader2,
  Search,
  Sparkles,
  Type,
  Zap,
} from 'lucide-react';
import type { ParsedJobOffer } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type { JobInputMode } from '../../hooks/useJobTarget';

export interface JobTargetSectionProps {
  t: getTType;
  isMobile: boolean;

  // Section expand/collapse
  isExpanded: boolean;
  onToggleExpanded: () => void;

  // Extension install prompt
  extensionInstalled: boolean;
  onShowExtensionPage: () => void;

  // Job target state (from useJobTarget)
  jobInputMode: JobInputMode;
  setJobInputMode: (mode: JobInputMode) => void;
  parsedJobOffer: ParsedJobOffer | null;
  setParsedJobOffer: (offer: ParsedJobOffer | null) => void;
  jobDescription: string;
  setJobDescription: (v: string) => void;
  jobUrl: string;
  setJobUrl: (v: string) => void;
  jobTitle: string;
  setJobTitle: (v: string) => void;

  // Actions
  canParseJobWithAi: boolean;
  isParsingJobWithAi: boolean;
  onParseJobWithAi: () => void | Promise<void>;
  isAutoIaRunning: boolean;
  onAutoIa: () => void | Promise<void>;

  // Derived
  effectiveJobDescription: string;
}

export const JobTargetSection = memo(function JobTargetSection(props: JobTargetSectionProps) {
  const {
    t,
    isMobile,
    isExpanded,
    onToggleExpanded,
    extensionInstalled,
    onShowExtensionPage,
    jobInputMode,
    setJobInputMode,
    parsedJobOffer,
    setParsedJobOffer,
    jobDescription,
    setJobDescription,
    jobUrl,
    setJobUrl,
    jobTitle,
    setJobTitle,
    canParseJobWithAi,
    isParsingJobWithAi,
    onParseJobWithAi,
    isAutoIaRunning,
    onAutoIa,
    effectiveJobDescription,
  } = props;

  // Unified smart input value (auto-detects URL / long text / title on blur)
  const unifiedValue =
    jobInputMode === 'url'
      ? jobUrl
      : jobInputMode === 'paste'
        ? jobDescription
        : jobTitle;
  const [unifiedDraft, setUnifiedDraft] = useState(unifiedValue);

  const handleUnifiedBlur = () => {
    const v = unifiedDraft.trim();
    if (!v) return;
    if (/^https?:\/\//i.test(v)) {
      setJobUrl(v);
      setJobInputMode('url');
    } else if (v.length > 200) {
      setJobDescription(v);
      setJobInputMode('paste');
      setParsedJobOffer(null);
    } else {
      setJobTitle(v);
      setJobInputMode('title');
      setParsedJobOffer(null);
    }
  };

  return (
    <div className="space-y-4">
      <h2
        className="flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4"
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
            <Search size={18} />
          </div>
          {t('ats.targetJob')}
        </span>
        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </h2>

      {isExpanded && (
        <div className="space-y-2">
          {/* Unified smart input */}
          <textarea
            className="h-16 text-sm leading-relaxed bg-black/40 border-white/5 focus:border-blue-500/50 placeholder:text-white/10 w-full rounded-lg border px-3 py-2 resize-none"
            placeholder={t('jobTarget.smartInputPlaceholder')}
            value={unifiedDraft}
            onChange={(e) => setUnifiedDraft(e.target.value)}
            onBlur={handleUnifiedBlur}
          />
          {unifiedDraft.trim() && (
            <p className="text-[10px] text-white/30">
              {/^https?:\/\//i.test(unifiedDraft.trim())
                ? t('jobTarget.hintUrl')
                : unifiedDraft.trim().length > 200
                  ? t('jobTarget.hintLongText')
                  : t('jobTarget.hintShortTitle')}
            </p>
          )}

          {/* Extension install prompt: desktop-only, hidden once installed */}
          {!isMobile ? (
            !extensionInstalled ? (
              <div className="p-3 rounded-lg border border-white/10 bg-white/5 space-y-2">
                <p className="text-sm text-white/70">
                  {t('jobTarget.extensionPrompt')}
                </p>
                <button
                  type="button"
                  onClick={onShowExtensionPage}
                  className="flex items-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <ExternalLink size={14} /> {t('jobTarget.installExtension')}
                </button>
              </div>
            ) : null
          ) : (
            <p className="text-sm text-white/50 italic">
              {t('jobTarget.extensionMobileTip')}
            </p>
          )}

          {/* Input mode selector */}
          <div className="flex gap-1 bg-white/5 p-1 rounded-lg border border-white/5">
            <button
              onClick={() => setJobInputMode('url')}
              className={`flex-1 py-1.5 px-2 text-sm font-bold rounded-md transition-all flex items-center justify-center gap-1 ${
                jobInputMode === 'url'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              <LinkIcon size={10} /> URL
            </button>
            <button
              onClick={() => {
                setJobInputMode('paste');
                setParsedJobOffer(null);
              }}
              className={`flex-1 py-1.5 px-2 text-sm font-bold rounded-md transition-all flex items-center justify-center gap-1 ${
                jobInputMode === 'paste'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              <FileEdit size={10} /> {t('jobTarget.modePaste')}
            </button>
            <button
              onClick={() => {
                setJobInputMode('title');
                setParsedJobOffer(null);
              }}
              className={`flex-1 py-1.5 px-2 text-sm font-bold rounded-md transition-all flex items-center justify-center gap-1 ${
                jobInputMode === 'title'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              <Type size={10} /> {t('jobTarget.modeTitle')}
            </button>
          </div>

          {/* Parsed offer summary (collapsible) */}
          {parsedJobOffer && (
            <details
              className="rounded-lg border border-white/10 bg-white/5 overflow-hidden group"
              open={false}
            >
              <summary className="cursor-pointer p-3 text-sm font-medium text-blue-400 hover:bg-white/5 list-none flex items-center justify-between gap-2">
                <span className="truncate">
                  {[parsedJobOffer.titre, parsedJobOffer.entreprise, parsedJobOffer.lieu]
                    .filter(Boolean)
                    .join(' • ') || t('jobTarget.extractedOffer')}
                </span>
                <ChevronRight
                  size={12}
                  className="shrink-0 transition-transform group-open:rotate-90"
                />
              </summary>
              <div className="px-3 pb-3 pt-2 space-y-2 text-sm border-t border-white/5">
                {parsedJobOffer.titre && (
                  <div className="rounded-md border border-white/10 bg-white/5 p-2">
                    <span className="text-sm font-bold uppercase tracking-wider text-blue-400/90">
                      {t('jobTarget.fieldTitle')}
                    </span>
                    <p className="text-white/90 mt-1">{parsedJobOffer.titre}</p>
                  </div>
                )}
                {parsedJobOffer.entreprise && (
                  <div className="rounded-md border border-white/10 bg-white/5 p-2">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-blue-400/90">
                      {t('jobTarget.fieldCompany')}
                    </span>
                    <p className="text-white/90 mt-1">{parsedJobOffer.entreprise}</p>
                  </div>
                )}
                {(parsedJobOffer.lieu || parsedJobOffer.salaire || parsedJobOffer.typePoste) && (
                  <div className="rounded-md border border-white/10 bg-white/5 p-2 space-y-1">
                    {parsedJobOffer.lieu && (
                      <p className="text-white/90">
                        <span className="text-white/50">{t('jobTarget.fieldLocation')}</span> {parsedJobOffer.lieu}
                      </p>
                    )}
                    {parsedJobOffer.salaire && (
                      <p className="text-white/90">
                        <span className="text-white/50">{t('jobTarget.fieldSalary')}</span> {parsedJobOffer.salaire}
                      </p>
                    )}
                    {parsedJobOffer.typePoste && (
                      <p className="text-white/90">
                        <span className="text-white/50">{t('jobTarget.fieldType')}</span> {parsedJobOffer.typePoste}
                      </p>
                    )}
                  </div>
                )}
                {parsedJobOffer.competencesRequises &&
                  parsedJobOffer.competencesRequises.length > 0 && (
                    <div className="rounded-md border border-white/10 bg-white/5 p-2">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-blue-400/90">
                        {t('skills.label')}
                      </span>
                      <p className="text-white/90 mt-1 break-words">
                        {Array.isArray(parsedJobOffer.competencesRequises)
                          ? parsedJobOffer.competencesRequises.join(', ')
                          : String(parsedJobOffer.competencesRequises)}
                      </p>
                    </div>
                  )}
                {parsedJobOffer.missionsPrincipales && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-sm font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldMissions')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-32 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.missionsPrincipales}
                    </p>
                  </details>
                )}
                {parsedJobOffer.profilRecherche && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-sm font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldProfile')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-32 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.profilRecherche}
                    </p>
                  </details>
                )}
                {parsedJobOffer.qualitesHumaines && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-sm font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldQualities')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-32 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.qualitesHumaines}
                    </p>
                  </details>
                )}
                {parsedJobOffer.avantages && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-sm font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldBenefits')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-28 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.avantages}
                    </p>
                  </details>
                )}
                {parsedJobOffer.aProposEntreprise && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-[9px] font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldAboutCompany')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-28 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.aProposEntreprise}
                    </p>
                  </details>
                )}
                {parsedJobOffer.description && (
                  <details className="rounded-md border border-white/10 bg-white/5 overflow-hidden">
                    <summary className="cursor-pointer p-2 text-sm font-bold uppercase tracking-wider text-blue-400/90 hover:bg-white/5">
                      {t('jobTarget.fieldFullDesc')}
                    </summary>
                    <p className="text-white/90 p-2 pt-0 whitespace-pre-wrap break-words max-h-40 overflow-y-auto border-t border-white/5">
                      {parsedJobOffer.description}
                    </p>
                  </details>
                )}
              </div>
            </details>
          )}

          {/* Input based on selected mode */}
          {jobInputMode === 'paste' && !parsedJobOffer && (
            <textarea
              className="h-24 text-sm leading-relaxed bg-black/40 border-white/5 focus:border-blue-500/50 placeholder:text-white/10"
              placeholder={t('ats.pasteOffer')}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
            />
          )}

          {jobInputMode === 'url' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <input
                  type="url"
                  className="flex-1 min-w-0 text-sm bg-black/40 border-white/5 focus:border-blue-500/50 placeholder:text-white/10"
                  placeholder="https://fr.indeed.com/viewjob?jk=..."
                  value={jobUrl}
                  onChange={(e) => setJobUrl(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    const u = jobUrl.trim();
                    if (!u) return;
                    const urlNoHash = u.replace(/#.*$/, '');
                    window.location.href = urlNoHash + '#atsmycv_fetch';
                  }}
                  disabled={!jobUrl.trim()}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-bold rounded-lg transition-all flex items-center gap-1"
                >
                  <ExternalLink size={12} />
                  {t('jobTarget.openAndFetch')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const u = jobUrl.trim();
                    if (u) window.open(u, '_blank');
                  }}
                  disabled={!jobUrl.trim()}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-bold rounded-lg transition-all flex items-center gap-1"
                  title={t('ats.openOffer')}
                >
                  <ExternalLink size={12} />
                  {t('ats.openOfferButton')}
                </button>
              </div>
              <p className="text-sm text-white/30">
                <strong>{t('jobTarget.openAndFetch')}</strong> : {t('ats.openOfferDesc')}
              </p>
            </div>
          )}

          {jobInputMode === 'title' && (
            <div className="space-y-2">
              <input
                type="text"
                className="text-sm bg-black/40 border-white/5 focus:border-blue-500/50 placeholder:text-white/10"
                placeholder={t('ats.jobTitlePlaceholder')}
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
              <p className="text-sm text-white/30">
                {t('jobTarget.titleModeHint')}
              </p>
            </div>
          )}

          {/* AI parse + Auto IA buttons */}
          {canParseJobWithAi && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void onParseJobWithAi()}
                disabled={isParsingJobWithAi || isAutoIaRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-bold rounded-lg transition-all"
              >
                {isParsingJobWithAi ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Sparkles size={12} />
                )}
                {t('jobTarget.parseWithAi')}
              </button>
              <button
                type="button"
                onClick={() => void onAutoIa()}
                disabled={isParsingJobWithAi || isAutoIaRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-bold rounded-lg transition-all"
              >
                {isAutoIaRunning ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Zap size={12} />
                )}
                {t('jobTarget.autoIa')}
              </button>
            </div>
          )}

          {effectiveJobDescription && effectiveJobDescription.trim().length >= 3 && (
            <div className="flex items-center gap-1 text-sm text-green-400">
              <CheckCircle2 size={10} />
              <span>{t('jobTarget.charsAnalyzed').replace('{n}', String(effectiveJobDescription.length))}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
